import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Header from "@/components/Header";
import UploadZone from "@/components/UploadZone";
import ResumeList from "@/components/ResumeList";
import BrandSettings from "@/components/BrandSettings";
import HeroIllustration from "@/components/HeroIllustration";
import FloatingDecor from "@/components/FloatingDecor";
import CountUp from "@/components/CountUp";
import FeatureGrid from "@/components/FeatureGrid";
import ProcessSteps from "@/components/ProcessSteps";
import CoBrandStrip from "@/components/CoBrandStrip";
import { listResumes, csvExportUrl } from "@/lib/api";
import { sparkle } from "@/lib/celebrate";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Settings,
  ArrowDown,
  Users,
  FileText,
  Briefcase,
  CheckCircle2,
} from "lucide-react";

const PAGE_SIZE = 10;

export default function Dashboard() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(query);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listResumes({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        q: debouncedQ,
      });
      setData(res);
    } catch {
      toast.error("Failed to load resumes");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const stats = useMemo(
    () => [
      {
        icon: FileText,
        label: "Total Parsed",
        value: data.total,
        hint: "Resumes in archive",
      },
      {
        icon: Briefcase,
        label: "Unique Companies",
        value: new Set(
          data.items.flatMap((r) =>
            (r.experience || []).map((e) => e.company).filter(Boolean)
          )
        ).size,
        hint: "From current page",
      },
      {
        icon: Users,
        label: "Active Candidates",
        value: data.items.length,
        hint: "On this page",
      },
      {
        icon: CheckCircle2,
        label: "Output Formats",
        value: "PDF · DOCX",
        hint: "ATS-compliant",
        isText: true,
      },
    ],
    [data]
  );

  const scrollToUpload = () => {
    document.getElementById("upload-section")?.scrollIntoView({ behavior: "smooth" });
  };

  // Debounce sparkle on stat hover to avoid runaway confetti during cursor jitter
  const lastSparkleAtRef = useRef(0);
  const maybeSparkle = useCallback(() => {
    const now = Date.now();
    if (now - lastSparkleAtRef.current > 2500 && data.total > 0) {
      lastSparkleAtRef.current = now;
      sparkle();
    }
  }, [data.total]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* === Hero === */}
        <section className="mb-14 fade-in relative bg-hero-dark rounded-lg overflow-hidden">
          <FloatingDecor />
          <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center z-10 p-8 md:p-12">
            <div>
              <span
                className="corp-badge mb-5 inline-flex animate-bounce-in"
                style={{
                  background: "rgba(0,167,139,0.18)",
                  color: "#7FE0CD",
                  borderColor: "rgba(0,167,139,0.4)",
                }}
                data-testid="hero-badge"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--brand-primary)] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--brand-primary)]" />
                </span>
                ITHR TalentLens · Powered by Claude 4.5
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.4rem] font-bold tracking-tight text-white leading-[1.05]">
                See every candidate{" "}
                <span className="relative inline-block">
                  <span className="gradient-text-warm">in focus.</span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 200 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 9 Q 50 1, 100 6 T 198 4"
                      stroke="#00A78B"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <br />
                One lens. Every resume.
              </h1>
              <p className="text-base md:text-lg text-[var(--text-on-dark-muted)] leading-relaxed mt-6 max-w-xl">
                <span className="font-semibold text-white">ITHR TalentLens</span>{" "}
                turns raw resumes into beautifully branded, contact-scrubbed
                dossiers in seconds. Upload one or many — we focus, you decide.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={scrollToUpload}
                  className="btn-primary btn-celebrate"
                  data-testid="hero-cta-button"
                >
                  <ArrowDown className="w-4 h-4" strokeWidth={2} />
                  Start Parsing
                </button>
                <a
                  href="#process"
                  className="btn-secondary"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    color: "#FFFFFF",
                    borderColor: "rgba(255,255,255,0.18)",
                  }}
                >
                  How it works
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[var(--text-on-dark-muted)]">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                  GDPR · contact data stripped
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                  Logo & footer branded
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand-primary)]" />
                  PDF + DOCX export
                </span>
              </div>
            </div>
            <div className="relative">
              <HeroIllustration className="w-full max-w-[520px] mx-auto drop-shadow-lg" />
            </div>
          </div>
        </section>

        {/* === Stats strip === */}
        <section className="mb-14 fade-in" data-testid="stats-strip">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className="swiss-card lift-on-hover p-5 flex items-start gap-4 cursor-default"
                  data-testid={`stat-${i}`}
                  onMouseEnter={() => i === 0 && maybeSparkle()}
                >
                  <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[var(--brand-primary)]/15 to-[var(--brand-accent)]/15 text-[var(--brand-primary)] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-medium text-[var(--text-muted)]">
                      {s.label}
                    </div>
                    <div
                      className={`font-heading font-bold text-[var(--text-primary)] mt-0.5 ${
                        s.isText ? "text-base" : "text-2xl"
                      }`}
                    >
                      <CountUp value={s.value} />
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                      {s.hint}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* === Brand Settings panel === */}
        <section className="mb-14 fade-in">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
            <div>
              <span className="overline">01 · brand kit</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-2">
                Your brand, on every document.
              </h2>
            </div>
            <button
              onClick={() => setShowSettings((s) => !s)}
              className="btn-secondary"
              data-testid="toggle-settings-button"
            >
              <Settings className="w-4 h-4" strokeWidth={1.75} />
              {showSettings ? "Hide settings" : "Manage brand kit"}
            </button>
          </div>
          {showSettings && <BrandSettings />}
        </section>

        {/* === Process steps === */}
        <ProcessSteps />

        {/* === Upload === */}
        <section id="upload-section" className="mb-14 fade-in">
          <div className="mb-5">
            <span className="overline">04 · ingest</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-2">
              Upload queue.
            </h2>
          </div>
          <UploadZone onUploaded={() => refresh()} />
        </section>

        {/* === Features === */}
        <FeatureGrid />

        {/* === List === */}
        <section className="fade-in">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
            <div>
              <span className="overline">05 · archive</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-2">
                Parsed resumes.
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, title, skill…"
                className="swiss-input min-w-[260px]"
                data-testid="search-input"
              />
              <a
                href={csvExportUrl()}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4" strokeWidth={1.75} />
                Export CSV
              </a>
              <button
                onClick={refresh}
                className="btn-secondary"
                data-testid="refresh-button"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="swiss-card p-12 text-center" data-testid="resume-list-loading">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-white mb-4 animate-float">
                <RefreshCw className="w-6 h-6 animate-spin" strokeWidth={1.75} />
              </div>
              <div className="font-heading font-semibold text-base text-[var(--text-primary)]">
                Sharpening pencils…
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                Loading your archive
              </div>
            </div>
          ) : (
            <>
              <ResumeList resumes={data.items} onChanged={refresh} />
              {data.total > 0 && totalPages > 1 && (
                <div
                  className="mt-4 flex flex-wrap items-center justify-between gap-3"
                  data-testid="pagination-controls"
                >
                  <span className="text-xs text-[var(--text-muted)] font-medium">
                    Showing {page * PAGE_SIZE + 1}–
                    {Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="btn-ghost"
                      data-testid="page-prev-button"
                    >
                      <ChevronLeft className="w-4 h-4" /> Prev
                    </button>
                    <span className="px-3 py-2 text-xs font-medium rounded-md border border-[var(--border-default)] bg-white">
                      Page {page + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      className="btn-ghost"
                      data-testid="page-next-button"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <footer className="mt-24 pt-8 border-t border-[var(--border-default)] flex flex-col md:flex-row justify-between gap-4 text-xs text-[var(--text-muted)]">
          <div className="flex items-center gap-2.5">
            <BrandLogo size={28} />
            <span className="font-medium text-[var(--text-secondary)]">
              ITHR TalentLens · ITHR Technologies Consulting LLC
            </span>
          </div>
          <span>Built with Claude Sonnet 4.5 · See talent clearly</span>
        </footer>
      </main>
      <CoBrandStrip />
    </div>
  );
}
